import React, { useState, useEffect, useContext, useRef } from 'react';
import {
  Box,
  Typography,
  Button,
  IconButton,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Switch,
  FormControlLabel,
  Divider,
  Chip,
  CircularProgress,
  Alert,
  Grid,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Tooltip,
  Card,
  CardContent
} from '@mui/material';
import {
  Add,
  Delete,
  Edit,
  Save,
  Close,
  VerifiedUser
} from '@mui/icons-material';
import ApiContext from 'contexts/ApiContext';
import useApi from 'hooks/useApi';

// ============================================================================
// INTERFACES
// ============================================================================

interface VerificationSettingsProps {
  teamUid: string;
  clientUID?: string;
}

interface SourceConfig {
  sourceType: 'step_image_field';
  stepUid?: string;
  fieldUid?: string;
}

interface NotificationConfig {
  users: string[];
  roles: string[];
  channels: string[];
  transactionOnly?: boolean;
}

type DocumentType = 'ID' | 'DRIVING_LICENSE' | 'PASSPORT';

interface VerificationCondition {
  uid?: string;
  name: string;
  provider: 'jumio';
  source: SourceConfig;
  sourceBack?: SourceConfig;
  twoSided?: boolean;
  country?: string;
  documentType: DocumentType;
  notification: NotificationConfig;
  enabled: boolean;
}

// ============================================================================
// CONSTANTS & HELPERS
// ============================================================================

const DOCUMENT_TYPES: { label: string; value: DocumentType }[] = [
  { label: 'ID Card', value: 'ID' },
  { label: 'Driving License', value: 'DRIVING_LICENSE' },
  { label: 'Passport', value: 'PASSPORT' },
];

const DOCUMENT_TYPE_LABELS: Record<string, string> = {
  ID: 'ID Card',
  DRIVING_LICENSE: 'Driving License',
  PASSPORT: 'Passport',
};

const NOTIFICATION_CHANNELS = ['EMAIL', 'SMS', 'IN-APP', 'PRIORITY', 'ALERT'];

const emptySource = (): SourceConfig => ({ sourceType: 'step_image_field', stepUid: '', fieldUid: '' });
const emptyNotification = (): NotificationConfig => ({ users: [], roles: [], channels: [], transactionOnly: false });
const emptyCondition = (): VerificationCondition => ({
  name: '', provider: 'jumio', source: emptySource(), sourceBack: emptySource(), twoSided: false, country: 'USA', documentType: 'ID', notification: emptyNotification(), enabled: true,
});

const safeCondition = (c: any): VerificationCondition => ({
  uid: c?.uid,
  name: c?.name || '',
  provider: 'jumio',
  source: { ...emptySource(), ...(c?.source || {}), sourceType: 'step_image_field' },
  sourceBack: { ...emptySource(), ...(c?.sourceBack || {}), sourceType: 'step_image_field' },
  // Two-sided is inferred from a persisted back field (the backend keys off sourceBack).
  twoSided: !!(c?.sourceBack?.stepUid && c?.sourceBack?.fieldUid),
  country: c?.country || 'USA',
  documentType: (['ID', 'DRIVING_LICENSE', 'PASSPORT'].includes(c?.documentType) ? c.documentType : 'ID') as DocumentType,
  notification: { ...emptyNotification(), ...(c?.notification || {}) },
  enabled: c?.enabled !== undefined ? c.enabled : true,
});

// ============================================================================
// COMPONENT
// ============================================================================

const VerificationSettings: React.FC<VerificationSettingsProps> = ({ teamUid, clientUID }) => {
  const apiContext = useContext(ApiContext);
  const { showToast } = useApi();
  const formRef = useRef<HTMLDivElement>(null);

  const [conditions, setConditions] = useState<VerificationCondition[]>([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [resourceTypes, setResourceTypes] = useState<any[]>([]);
  const [steps, setSteps] = useState<any[]>([]);
  const [error, setError] = useState<string | null>(null);

  // Form state: null = no form, condition object = editing/creating
  const [editingCondition, setEditingCondition] = useState<VerificationCondition | null>(null);
  const [editingIndex, setEditingIndex] = useState<number | null>(null); // null = new, number = editing existing

  useEffect(() => { if (teamUid) loadData(); }, [teamUid]);

  const loadData = async () => {
    if (!apiContext || !teamUid) { setLoading(false); return; }
    setLoading(true);
    setError(null);
    try {
      const results = await Promise.allSettled([
        apiContext.getVerificationConfig(teamUid),
        apiContext.getResourceTypes(clientUID, 'user'),
        apiContext.getFlowByTeamUid(teamUid),
        apiContext.getDefaultSteps(teamUid),
      ]);
      const configRes = results[0].status === 'fulfilled' ? results[0].value : null;
      const configData = configRes?.data;
      const configArray = Array.isArray(configData) ? configData : Array.isArray(configRes) ? configRes : [];
      setConditions(configArray.map(safeCondition));

      const rtData = results[1].status === 'fulfilled' ? results[1].value?.data : null;
      setResourceTypes(Array.isArray(rtData) ? rtData : []);

      const flowSteps = results[2].status === 'fulfilled' ? results[2].value?.data?.steps : null;
      // Role-based default steps can also be verification sources. Normalize them to
      // the flow-step shape ({ uid, title, stepType, component.fields }) and tag the
      // title so admins can distinguish them in the step picker. getStepFields and
      // the step <Select>s then work for both without further changes.
      const dsRes = results[3].status === 'fulfilled' ? (results[3].value?.data ?? results[3].value) : null;
      const defaultSteps = (Array.isArray(dsRes) ? dsRes : []).map((d: any) => ({
        uid: d.uid,
        title: `${d.title} (Default Step)`,
        stepType: d.stepType,
        component: { componentType: d.stepType, fields: d.fields || [] },
      }));
      setSteps([...(Array.isArray(flowSteps) ? flowSteps : []), ...defaultSteps]);
    } catch (err: any) {
      setError(err?.message || 'Failed to load');
    } finally {
      setLoading(false);
    }
  };

  // ==================== ACTIONS ====================

  const handleAdd = () => {
    setEditingCondition(emptyCondition());
    setEditingIndex(null);
    setTimeout(() => formRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' }), 100);
  };

  const handleEdit = (index: number) => {
    setEditingCondition(safeCondition(conditions[index]));
    setEditingIndex(index);
    setTimeout(() => formRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' }), 100);
  };

  const handleCancel = () => {
    setEditingCondition(null);
    setEditingIndex(null);
  };

  const handleDelete = async (index: number) => {
    const condition = conditions[index];
    if (condition.uid && apiContext) {
      try {
        await apiContext.deleteVerificationConfig(teamUid, condition.uid);
        showToast('Verification rule deleted', 'success');
      } catch (err) { showToast('Failed to delete', 'error'); }
    }
    const updated = conditions.filter((_, i) => i !== index);
    setConditions(updated);
    if (editingIndex === index) handleCancel();
  };

  const handleSave = async () => {
    if (!apiContext || !editingCondition) return;
    setSaving(true);
    try {
      // Persist the back field + country only for two-sided rules; otherwise null
      // them so the backend treats it as single-sided (null clears on update).
      const payload = {
        ...editingCondition,
        sourceBack: editingCondition.twoSided ? editingCondition.sourceBack : null,
        country: editingCondition.twoSided ? (editingCondition.country || 'USA') : null,
      };
      let res: any;
      if (editingCondition.uid) {
        res = await apiContext.updateVerificationConfig(teamUid, editingCondition.uid, payload);
      } else {
        res = await apiContext.createVerificationConfig(teamUid, payload);
      }
      // Backend returns HTTP 200 with { success:false } on validation errors, so
      // axios doesn't throw — surface the failure instead of a false "saved".
      if (res && res.success === false) {
        showToast(typeof res.data === 'string' ? res.data : (res.message || 'Failed to save'), 'error');
        return;
      }
      const savedData = res?.data;
      const safe = safeCondition(savedData?.uid ? savedData : { ...editingCondition, uid: savedData?.uid || editingCondition.uid });

      if (editingIndex !== null) {
        const updated = [...conditions];
        updated[editingIndex] = safe;
        setConditions(updated);
      } else {
        setConditions([...conditions, safe]);
      }
      setEditingCondition(null);
      setEditingIndex(null);
      showToast('Verification rule saved', 'success');
    } catch (err) {
      showToast('Failed to save', 'error');
    } finally {
      setSaving(false);
    }
  };

  // ==================== FORM STATE UPDATERS ====================

  const updateField = (updates: Partial<VerificationCondition>) => {
    if (!editingCondition) return;
    setEditingCondition({ ...editingCondition, ...updates });
  };

  const updateSource = (updates: Partial<SourceConfig>) => {
    if (!editingCondition) return;
    setEditingCondition({ ...editingCondition, source: { ...editingCondition.source, ...updates } });
  };

  const updateSourceBack = (updates: Partial<SourceConfig>) => {
    if (!editingCondition) return;
    setEditingCondition({ ...editingCondition, sourceBack: { ...(editingCondition.sourceBack || emptySource()), ...updates } });
  };

  const updateNotification = (updates: Partial<NotificationConfig>) => {
    if (!editingCondition) return;
    setEditingCondition({ ...editingCondition, notification: { ...editingCondition.notification, ...updates } });
  };

  // ==================== VALIDATION ====================

  const isFormValid = (): boolean => {
    if (!editingCondition) return false;
    if (!editingCondition.name?.trim()) return false;
    if (!editingCondition.source.stepUid || !editingCondition.source.fieldUid) return false;
    if (editingCondition.twoSided && (!editingCondition.sourceBack?.stepUid || !editingCondition.sourceBack?.fieldUid)) return false;
    return true;
  };

  // ==================== HELPERS ====================

  const getStepFields = (stepUid: string, fieldType?: 'text' | 'image' | 'date') => {
    const step = steps.find((s: any) => s.uid === stepUid);
    if (!step?.component?.fields) return [];
    const imageComponents = ['CAMERA', 'FILE'];
    const textComponents = ['TEXT', 'NUMBER', 'SELECT', 'CHECKBOX', 'RADIO', 'DATE', 'TEXTARRAY', 'SWITCH', 'RESOURCE', 'PASSWORD', 'ESIGN'];
    if (!fieldType) return step.component.fields;
    if (fieldType === 'image') {
      const filtered = step.component.fields.filter((f: any) => imageComponents.includes(f.inputComponent));
      // If no explicit image fields, but step type is IMAGE/DOCUMENT, show all fields
      if (filtered.length === 0 && ['IMAGE', 'DOCUMENT'].includes(step.component?.componentType || step.stepType)) {
        return step.component.fields;
      }
      return filtered;
    }
    if (fieldType === 'date') {
      return step.component.fields.filter((f: any) => f.inputComponent === 'DATE');
    }
    return step.component.fields.filter((f: any) => textComponents.includes(f.inputComponent));
  };

  // ==================== RENDER: SOURCE ROW ====================

  const renderSourceRow = (source: SourceConfig, onUpdate: (u: Partial<SourceConfig>) => void) => (
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1, p: 1, bgcolor: 'grey.50', borderRadius: 1, border: '1px solid', borderColor: 'grey.200' }}>
      <FormControl size="small" sx={{ minWidth: 130, flex: 1 }}>
        <InputLabel>Step</InputLabel>
        <Select value={source.stepUid || ''} label="Step"
          onChange={(e) => onUpdate({ stepUid: e.target.value, fieldUid: '' })}>
          {steps.map((s: any) => <MenuItem key={s.uid} value={s.uid}>{s.title}</MenuItem>)}
        </Select>
      </FormControl>
      <FormControl size="small" sx={{ minWidth: 130, flex: 1 }}>
        <InputLabel>Image Field</InputLabel>
        <Select value={source.fieldUid || ''} label="Image Field" disabled={!source.stepUid}
          onChange={(e) => onUpdate({ fieldUid: e.target.value })}>
          {source.stepUid && (() => {
            const fields = getStepFields(source.stepUid, 'image');
            if (fields.length === 0) return <MenuItem disabled><em>No image fields in this step</em></MenuItem>;
            return fields.map((f: any) => <MenuItem key={f.uid} value={f.uid}>{f.question || f.name}</MenuItem>);
          })()}
        </Select>
      </FormControl>
    </Box>
  );

  // ==================== MAIN RENDER ====================

  if (loading) {
    return <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}><CircularProgress /></Box>;
  }

  return (
    <Box sx={{ p: 3 }}>
      {/* Header */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
        <Typography variant="h4" sx={{ fontWeight: 600 }}>Identity Verification</Typography>
      </Box>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        Mark an image field for identity-document verification via an external service. When the step is submitted, the image is sent for external verification and the result appears alongside detection results in the transaction details.
      </Typography>

      {error && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>{error}</Alert>}

      {/* ==================== LIST TABLE ==================== */}
      <TableContainer component={Paper} elevation={0} sx={{ mb: 3, border: '1px solid', borderColor: 'grey.200', borderRadius: 2, overflow: 'hidden' }}>
        <Table size="small">
          <TableHead>
            <TableRow sx={{ bgcolor: 'grey.100' }}>
              <TableCell sx={{ fontWeight: 600, color: 'text.secondary', fontSize: '0.75rem', textTransform: 'uppercase', letterSpacing: 0.5, py: 1.5 }}>Name</TableCell>
              <TableCell sx={{ fontWeight: 600, color: 'text.secondary', fontSize: '0.75rem', textTransform: 'uppercase', letterSpacing: 0.5, py: 1.5 }}>Image Field</TableCell>
              <TableCell sx={{ fontWeight: 600, color: 'text.secondary', fontSize: '0.75rem', textTransform: 'uppercase', letterSpacing: 0.5, py: 1.5 }}>Document Type</TableCell>
              <TableCell sx={{ fontWeight: 600, color: 'text.secondary', fontSize: '0.75rem', textTransform: 'uppercase', letterSpacing: 0.5, py: 1.5 }}>Status</TableCell>
              <TableCell align="center" sx={{ fontWeight: 600, color: 'text.secondary', fontSize: '0.75rem', textTransform: 'uppercase', letterSpacing: 0.5, py: 1.5 }}>Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {conditions.length === 0 ? (
              <TableRow>
                <TableCell colSpan={5} align="center" sx={{ py: 4, color: 'text.secondary' }}>
                  No verification rules configured
                </TableCell>
              </TableRow>
            ) : (
              conditions.map((c, idx) => {
                const step = steps.find((s: any) => s.uid === c.source.stepUid);
                const field = step?.component?.fields?.find((f: any) => f.uid === c.source.fieldUid);
                const fieldLabel = field ? (field.question || field.name) : (c.source.fieldUid || '—');
                return (
                  <TableRow key={c.uid || idx} hover
                    sx={{
                      '&:last-child td': { borderBottom: 0 },
                      ...(editingIndex === idx && { bgcolor: 'primary.50', '&:hover': { bgcolor: 'primary.50' } }),
                    }}>
                    <TableCell sx={{ py: 1.5, fontWeight: 500 }}>{c.name || <Typography component="span" color="text.disabled" sx={{ fontStyle: 'italic' }}>Unnamed</Typography>}</TableCell>
                    <TableCell sx={{ py: 1.5 }}>
                      {c.source.stepUid && c.source.fieldUid ? (
                        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                          <Chip size="small" variant="outlined" color="primary" label={`${step?.title ? `${step.title} · ` : ''}${fieldLabel}${c.sourceBack?.fieldUid ? ' · front' : ''}`} sx={{ fontSize: '0.7rem', height: 22 }} />
                          {c.sourceBack?.stepUid && c.sourceBack?.fieldUid && (() => {
                            const bStep = steps.find((s: any) => s.uid === c.sourceBack!.stepUid);
                            const bField = bStep?.component?.fields?.find((f: any) => f.uid === c.sourceBack!.fieldUid);
                            const bLabel = bField ? (bField.question || bField.name) : c.sourceBack!.fieldUid;
                            return <Chip size="small" variant="outlined" label={`${bLabel} · back`} sx={{ fontSize: '0.7rem', height: 22 }} />;
                          })()}
                        </Box>
                      ) : (
                        <Typography component="span" color="text.disabled" sx={{ fontStyle: 'italic' }}>Not set</Typography>
                      )}
                    </TableCell>
                    <TableCell sx={{ py: 1.5 }}>
                      <Chip size="small" label={DOCUMENT_TYPE_LABELS[c.documentType] || c.documentType} sx={{ fontSize: '0.7rem', height: 22 }} />
                    </TableCell>
                    <TableCell sx={{ py: 1.5 }}>
                      <Chip label={c.enabled ? 'Active' : 'Inactive'} size="small"
                        sx={{
                          fontWeight: 500, fontSize: '0.7rem', height: 22,
                          bgcolor: c.enabled ? 'success.light' : 'grey.200',
                          color: c.enabled ? 'success.dark' : 'text.secondary',
                        }} />
                    </TableCell>
                    <TableCell align="center" sx={{ py: 1.5, whiteSpace: 'nowrap' }}>
                      <Tooltip title="Edit">
                        <IconButton size="small" onClick={() => handleEdit(idx)}
                          sx={{ color: 'primary.main', '&:hover': { bgcolor: 'primary.50' } }}>
                          <Edit fontSize="small" />
                        </IconButton>
                      </Tooltip>
                      <Tooltip title="Delete">
                        <IconButton size="small" onClick={() => handleDelete(idx)}
                          sx={{ color: 'error.main', '&:hover': { bgcolor: 'error.50' } }}>
                          <Delete fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    </TableCell>
                  </TableRow>
                );
              })
            )}
          </TableBody>
        </Table>
      </TableContainer>

      {/* ==================== FORM ==================== */}
      <div ref={formRef}>
        {editingCondition ? (
          <Card variant="outlined" sx={{ border: '1px solid', borderColor: 'primary.main' }}>
            <CardContent sx={{ p: 3 }}>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                <Typography variant="h6" sx={{ fontWeight: 600 }}>
                  {editingIndex !== null ? 'Edit Verification Rule' : 'New Verification Rule'}
                </Typography>
                <IconButton size="small" onClick={handleCancel}><Close /></IconButton>
              </Box>

              {/* Name + Enabled */}
              <Box sx={{ display: 'flex', gap: 2, mb: 2.5 }}>
                <TextField size="small" label="Rule Name" value={editingCondition.name}
                  onChange={(e) => updateField({ name: e.target.value })}
                  placeholder="e.g. License Verification" sx={{ flex: 1 }}
                  required error={!editingCondition.name.trim()} />
                <FormControlLabel
                  control={<Switch checked={editingCondition.enabled} onChange={(e) => updateField({ enabled: e.target.checked })} />}
                  label="Enabled" sx={{ mr: 0 }} />
              </Box>

              <Chip
                icon={<VerifiedUser sx={{ fontSize: 16 }} />}
                label="Uses External Identity Verification"
                size="small"
                color="success"
                variant="outlined"
                sx={{ mb: 2.5, fontWeight: 500 }}
              />

              <Divider sx={{ mb: 2 }} />

              {/* Image field source (front) */}
              <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 0.25 }}>
                Front image field <span style={{ color: '#f44336' }}>*</span>
              </Typography>
              <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1 }}>
                The image field whose document is sent for external verification when the step is submitted.
              </Typography>
              {renderSourceRow(editingCondition.source, updateSource)}

              {/* Two-sided document (front + back) — needed for the DMV/AAMVA check */}
              <FormControlLabel
                sx={{ mt: 0.5 }}
                control={
                  <Switch
                    size="small"
                    checked={!!editingCondition.twoSided}
                    onChange={(e) => updateField({ twoSided: e.target.checked })}
                  />
                }
                label={
                  <Typography variant="caption">
                    Two-sided document (front + back) — both sides are sent together; required for the DMV / AAMVA check
                  </Typography>
                }
              />
              {editingCondition.twoSided && (
                <Box sx={{ mt: 1 }}>
                  <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 0.5 }}>
                    Back image field <span style={{ color: '#f44336' }}>*</span>
                  </Typography>
                  {renderSourceRow(editingCondition.sourceBack || emptySource(), updateSourceBack)}
                  <TextField
                    size="small"
                    label="Document country (ISO-3)"
                    value={editingCondition.country || ''}
                    onChange={(e) => updateField({ country: e.target.value.toUpperCase() })}
                    helperText="DMV / AAMVA is US-only → USA."
                    sx={{ minWidth: 220, mt: 1 }}
                  />
                </Box>
              )}

              <Divider sx={{ mb: 2, mt: 1 }} />

              {/* Document Type */}
              <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 0.25 }}>
                Document Type <span style={{ color: '#f44336' }}>*</span>
              </Typography>
              <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1 }}>
                The kind of identity document the external service should expect.
              </Typography>
              <FormControl size="small" sx={{ minWidth: 220, mb: 2.5 }}>
                <InputLabel>Document Type</InputLabel>
                <Select value={editingCondition.documentType} label="Document Type"
                  onChange={(e) => updateField({ documentType: e.target.value as DocumentType })}>
                  {DOCUMENT_TYPES.map((d) => <MenuItem key={d.value} value={d.value}>{d.label}</MenuItem>)}
                </Select>
              </FormControl>

              <Divider sx={{ mb: 2 }} />

              {/* Notification (Optional) */}
              <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 0.5 }}>
                Notification <Typography component="span" variant="caption" color="text.secondary">(Optional)</Typography>
              </Typography>
              <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1.5 }}>
                If configured, the verification result will be sent to the selected recipients.
              </Typography>
              <Grid container spacing={2} sx={{ mb: 2 }}>
                <Grid item xs={12}>
                  <FormControl fullWidth size="small">
                    <InputLabel>Channels</InputLabel>
                    <Select multiple value={editingCondition.notification.channels || []} label="Channels"
                      onChange={(e) => updateNotification({ channels: e.target.value as string[] })}
                      renderValue={(sel) => (
                        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                          {(sel as string[]).map((v) => <Chip key={v} label={v} size="small" />)}
                        </Box>
                      )}>
                      {NOTIFICATION_CHANNELS.map((ch) => <MenuItem key={ch} value={ch}>{ch}</MenuItem>)}
                    </Select>
                  </FormControl>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <FormControl fullWidth size="small">
                    <InputLabel>Roles</InputLabel>
                    <Select multiple value={editingCondition.notification.roles || []} label="Roles"
                      onChange={(e) => updateNotification({ roles: e.target.value as string[] })}
                      renderValue={(sel) => (
                        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                          {(sel as string[]).map((v) => {
                            const rt = resourceTypes.find((r: any) => (r.id || r._id) === v);
                            return <Chip key={v} label={rt?.name || v} size="small" />;
                          })}
                        </Box>
                      )}>
                      {resourceTypes.map((rt: any) => (
                        <MenuItem key={rt.id || rt._id} value={rt.id || rt._id}>{rt.name}</MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Grid>
                <Grid item xs={12}>
                  <FormControlLabel
                    control={
                      <Switch
                        size="small"
                        checked={!!editingCondition.notification.transactionOnly}
                        onChange={(e) => updateNotification({ transactionOnly: e.target.checked })}
                      />
                    }
                    label={
                      <Typography variant="caption">
                        Only notify users in this transaction — role recipients are limited to drivers assigned to or active on this transaction (instead of everyone in the process).
                      </Typography>
                    }
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <FormControl fullWidth size="small">
                    <InputLabel>Users</InputLabel>
                    <Select multiple value={editingCondition.notification.users || []} label="Users"
                      onChange={(e) => updateNotification({ users: e.target.value as string[] })}
                      renderValue={(sel) => (
                        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                          {(sel as string[]).map((v) => <Chip key={v} label={v} size="small" />)}
                        </Box>
                      )}>
                      <MenuItem disabled><em>User selection from process context</em></MenuItem>
                    </Select>
                  </FormControl>
                </Grid>
              </Grid>

              {/* Actions */}
              <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 1, pt: 1 }}>
                <Button variant="outlined" onClick={handleCancel}>Cancel</Button>
                <Button variant="contained" startIcon={<Save />} onClick={handleSave} disabled={saving || !isFormValid()}>
                  {saving ? 'Saving...' : editingIndex !== null ? 'Update Rule' : 'Save Rule'}
                </Button>
              </Box>
            </CardContent>
          </Card>
        ) : (
          <Button variant="contained" startIcon={<Add />} onClick={handleAdd}>
            Add Verification Rule
          </Button>
        )}
      </div>
    </Box>
  );
};

export default VerificationSettings;
